import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { RootState } from '../index'
import { enableMapSet } from 'immer'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { Content } from '../../types/models/content'
import { migrateBoundingBoxToPoints } from '../../utils/helpers'

// Helper function for Redux slice - migration without image dimensions
const migrateBoundingBoxForRedux = (box: AnnotationDataBoundingBox): AnnotationDataBoundingBox => {
  return migrateBoundingBoxToPoints(box)
}

// Enable support for Map and Set in Immer
enableMapSet()

export enum ViewType {
  Grid = 'grid',
  List = 'list',
  Slideshow = 'slideshow',
}

export enum SortOrder {
  Asc = 'asc',
  Desc = 'desc',
}

export enum BBoxStatus {
  Untouched = 'untouched',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

const ZOOM_LIMITS = {
  MIN: 1,
  MAX: 5,
} as const

export interface UndoStackItem {
  boundingBoxStatuses?: Record<number, BBoxStatus>
  rejected: number[]
  accepted: number[]
  all: AnnotationDataBoundingBox[]
  editingTagIds?: string[]
}

export interface AnnotationState {
  sortKey: string
  sortOrder: SortOrder
  view: ViewType
  zoomLevel: number
  annotatedContentIds: string[]
  selectedContentId: string

  undoStack: UndoStackItem[]
  redoStack: UndoStackItem[]

  allBoundingBoxes: AnnotationDataBoundingBox[]
  createdBoundingBoxes: AnnotationDataBoundingBox[]
  boundingBoxStatuses: Record<number, BBoxStatus>
  acceptedBBoxIndexes: number[] // Keep for backward compatibility
  rejectedBBoxIndexes: number[] // Keep for backward compatibility
  selectedBoundingBox: AnnotationDataBoundingBox | undefined

  initialBoundingBoxes: AnnotationDataBoundingBox[]
  editingTagIds: string[]
  annotatedPage: number
  unannotatedPage: number
  predictionsPage: number
  editingContent: Content | undefined
}

const initialState: AnnotationState = {
  sortKey: 'created_at',
  sortOrder: SortOrder.Desc,
  view: ViewType.Grid,
  zoomLevel: 2,
  annotatedContentIds: [],
  selectedContentId: '',

  undoStack: [],
  redoStack: [],

  allBoundingBoxes: [],
  createdBoundingBoxes: [],
  boundingBoxStatuses: {},
  acceptedBBoxIndexes: [],
  rejectedBBoxIndexes: [],
  selectedBoundingBox: undefined,

  initialBoundingBoxes: [],
  editingTagIds: [],
  annotatedPage: 1,
  unannotatedPage: 1,
  predictionsPage: 1,
  editingContent: undefined,
}

const updateUniqueTagIds = (tagIds: string[], newTagId: string): string[] => {
  const updatedTagIds = new Set(tagIds)
  updatedTagIds.add(newTagId)
  return Array.from(updatedTagIds)
}

const annotationSlice = createSlice({
  name: 'annotation',
  initialState,

  reducers: {
    setSelectedBoundingBox: (
      state,
      action: PayloadAction<AnnotationDataBoundingBox | undefined>
    ) => {
      state.selectedBoundingBox = action.payload
    },

    setEditingContent: (state, action: PayloadAction<Content>) => {
      const { b64_image: omitted, ...rest } = action.payload
      state.editingContent = rest
    },
    clearEditingContent: (state) => {
      state.editingContent = undefined
    },

    clearUndoStack: (state) => {
      state.undoStack = []
    },

    clearRedoStack: (state) => {
      state.redoStack = []
    },

    // New action to set the status of a bounding box
    setBoundingBoxStatus: (
      state,
      action: PayloadAction<{ classIndex: number; status: BBoxStatus }>
    ) => {
      const { classIndex, status } = action.payload
      state.boundingBoxStatuses[classIndex] = status

      // Update the legacy index arrays for backward compatibility
      if (status === BBoxStatus.Accepted) {
        state.acceptedBBoxIndexes = Array.from(new Set([...state.acceptedBBoxIndexes, classIndex]))
        state.rejectedBBoxIndexes = state.rejectedBBoxIndexes.filter((idx) => idx !== classIndex)
      } else if (status === BBoxStatus.Rejected) {
        state.rejectedBBoxIndexes = Array.from(new Set([...state.rejectedBBoxIndexes, classIndex]))
        state.acceptedBBoxIndexes = state.acceptedBBoxIndexes.filter((idx) => idx !== classIndex)
      } else {
        // If untouched, remove from both arrays
        state.acceptedBBoxIndexes = state.acceptedBBoxIndexes.filter((idx) => idx !== classIndex)
        state.rejectedBBoxIndexes = state.rejectedBBoxIndexes.filter((idx) => idx !== classIndex)
      }
    },

    addAcceptedBoundingBoxByIndex: (state, action: PayloadAction<number>) => {
      state.acceptedBBoxIndexes = Array.from(
        new Set([...state.acceptedBBoxIndexes, action.payload])
      )
      state.rejectedBBoxIndexes = state.rejectedBBoxIndexes.filter(
        (index) => index !== action.payload
      )
      // Update the status map
      state.boundingBoxStatuses[action.payload] = BBoxStatus.Accepted
    },

    setAcceptedBoundingBoxes: (state, action: PayloadAction<number[]>) => {
      state.acceptedBBoxIndexes = Array.from(new Set(action.payload))
      // Update the status map
      action.payload.forEach((index) => {
        state.boundingBoxStatuses[index] = BBoxStatus.Accepted
      })
    },

    removeAcceptedBoundingBoxByIndex: (state, action: PayloadAction<number>) => {
      state.acceptedBBoxIndexes = state.acceptedBBoxIndexes.filter(
        (index) => index !== action.payload
      )
      // Update the status map if it's not in rejected either
      if (!state.rejectedBBoxIndexes.includes(action.payload)) {
        state.boundingBoxStatuses[action.payload] = BBoxStatus.Untouched
      }
    },

    addRejectedBoundingBoxByIndex: (state, action: PayloadAction<number>) => {
      state.rejectedBBoxIndexes = Array.from(
        new Set([...state.rejectedBBoxIndexes, action.payload])
      )
      state.acceptedBBoxIndexes = state.acceptedBBoxIndexes.filter(
        (index) => index !== action.payload
      )
      // Update the status map
      state.boundingBoxStatuses[action.payload] = BBoxStatus.Rejected
    },
    setRejectedBoundingBoxes: (state, action: PayloadAction<number[]>) => {
      state.rejectedBBoxIndexes = Array.from(new Set(action.payload))
      // Update the status map
      action.payload.forEach((index) => {
        state.boundingBoxStatuses[index] = BBoxStatus.Rejected
      })
    },
    removeRejectedBoundingBoxByIndex: (state, action: PayloadAction<number>) => {
      state.rejectedBBoxIndexes = state.rejectedBBoxIndexes.filter(
        (index) => index !== action.payload
      )
      // Update the status map if it's not in accepted either
      if (!state.acceptedBBoxIndexes.includes(action.payload)) {
        state.boundingBoxStatuses[action.payload] = BBoxStatus.Untouched
      }
    },

    setAnnotatedPage: (state, action: PayloadAction<number>) => {
      state.annotatedPage = action.payload
    },

    setPredictionsPage: (state, action: PayloadAction<number>) => {
      state.predictionsPage = action.payload
    },

    setUnannotatedPage: (state, action: PayloadAction<number>) => {
      state.unannotatedPage = action.payload
    },

    setSelectedContentId: (state, action: PayloadAction<string>) => {
      state.selectedContentId = action.payload
    },

    clearSelectedContentId: (state) => {
      state.selectedContentId = ''
    },

    setSortKey: (state, action: PayloadAction<string>) => {
      state.sortKey = action.payload
    },

    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc
    },

    zoomIn: (state) => {
      state.zoomLevel = Math.min(state.zoomLevel + 1, ZOOM_LIMITS.MAX) // Max zoom level
    },

    zoomOut: (state) => {
      state.zoomLevel = Math.max(state.zoomLevel - 1, 1) // Min zoom level
    },

    addAnnotatedContentIds: (state, action: PayloadAction<string[]>) => {
      const newIds = action.payload
      state.annotatedContentIds = Array.from(new Set([...state.annotatedContentIds, ...newIds]))
    },

    clearAnnotatedContentIds: (state) => {
      state.annotatedContentIds = []
    },

    setAllBoundingBoxes: (state, action: PayloadAction<AnnotationDataBoundingBox[]>) => {
      // Migrate all bounding boxes to ensure they have points (without image dimensions in Redux)
      const migratedBoxes = action.payload.map(migrateBoundingBoxForRedux)

      state.allBoundingBoxes = [...migratedBoxes].sort(
        (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
      )
      // Initialize all new boxes as untouched unless already in accepted/rejected
      migratedBoxes.forEach((box) => {
        if (state.boundingBoxStatuses[box.class_index] === undefined) {
          if (state.acceptedBBoxIndexes.includes(box.class_index)) {
            state.boundingBoxStatuses[box.class_index] = BBoxStatus.Accepted
          } else if (state.rejectedBBoxIndexes.includes(box.class_index)) {
            state.boundingBoxStatuses[box.class_index] = BBoxStatus.Rejected
          } else {
            state.boundingBoxStatuses[box.class_index] = BBoxStatus.Untouched
          }
        }
      })
    },

    mergeAllBoundingBoxes: (state, action: PayloadAction<AnnotationDataBoundingBox[]>) => {
      const uniqueMap = new Map<number, AnnotationDataBoundingBox>()

      // First add all existing boxes from state
      state.allBoundingBoxes.forEach((box) => {
        uniqueMap.set(box.class_index, box)
      })

      // Migrate new boxes and then add/override (without image dimensions in Redux)
      const migratedBoxes = action.payload.map(migrateBoundingBoxForRedux)
      migratedBoxes.forEach((box) => {
        uniqueMap.set(box.class_index, box)
        // Initialize new boxes as untouched unless already in accepted/rejected
        if (state.boundingBoxStatuses[box.class_index] === undefined) {
          if (state.acceptedBBoxIndexes.includes(box.class_index)) {
            state.boundingBoxStatuses[box.class_index] = BBoxStatus.Accepted
          } else if (state.rejectedBBoxIndexes.includes(box.class_index)) {
            state.boundingBoxStatuses[box.class_index] = BBoxStatus.Rejected
          } else {
            state.boundingBoxStatuses[box.class_index] = BBoxStatus.Untouched
          }
        }
      })

      // Update state with merged results
      state.allBoundingBoxes = Array.from(uniqueMap.values()).sort(
        (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
      )
    },

    clearAllBoundingBoxes: (state) => {
      state.allBoundingBoxes = []
      state.boundingBoxStatuses = {}
      state.acceptedBBoxIndexes = []
      state.rejectedBBoxIndexes = []
    },

    setInitialBoundingBoxes: (state, action: PayloadAction<AnnotationDataBoundingBox[]>) => {
      // Migrate all bounding boxes to ensure they have points (without image dimensions in Redux)
      const migratedBoxes = action.payload.map(migrateBoundingBoxForRedux)

      const sortedBoxes = [...migratedBoxes].sort(
        (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
      )
      state.initialBoundingBoxes = sortedBoxes
      // Initialize all statuses as untouched
      const initialStatuses: Record<number, BBoxStatus> = {}
      sortedBoxes.forEach((box) => {
        initialStatuses[box.class_index] = BBoxStatus.Untouched
      })
      state.boundingBoxStatuses = initialStatuses
    },

    clearInitialBoundingBoxes: (state) => {
      state.initialBoundingBoxes = []
    },

    resetAllBoundingBoxes: (state) => {
      state.allBoundingBoxes = [...state.initialBoundingBoxes]
      // Reset all statuses to untouched
      const resetStatuses: Record<number, BBoxStatus> = {}
      state.initialBoundingBoxes.forEach((box) => {
        resetStatuses[box.class_index] = BBoxStatus.Untouched
      })
      state.boundingBoxStatuses = resetStatuses
      state.acceptedBBoxIndexes = []
      state.rejectedBBoxIndexes = []
    },

    addBoundingBox: (state, action: PayloadAction<AnnotationDataBoundingBox>) => {
      // Migrate the bounding box to ensure it has points (without image dimensions in Redux)
      const migratedBBox = migrateBoundingBoxForRedux(action.payload)

      if (state.allBoundingBoxes) {
        state.allBoundingBoxes = [...state.allBoundingBoxes, { ...migratedBBox }].sort(
          (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
        )
      } else {
        state.allBoundingBoxes = [{ ...migratedBBox }].sort(
          (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
        )
      }
      // Set the new box as untouched
      state.boundingBoxStatuses[migratedBBox.class_index] = BBoxStatus.Untouched
      state.editingTagIds = updateUniqueTagIds(state.editingTagIds, migratedBBox.name)
    },

    updateBoundingBox: (
      state,
      action: PayloadAction<{ updatedBBox: AnnotationDataBoundingBox }>
    ) => {
      // Migrate the updated bounding box to ensure it has points (without image dimensions in Redux)
      const migratedBBox = migrateBoundingBoxForRedux(action.payload.updatedBBox)
      const { updatedBBox } = { updatedBBox: migratedBBox }
      const classIndex = updatedBBox.class_index
      if (classIndex === undefined || classIndex === -1) return
      // Find by class_index, not array position, because allBoundingBoxes is sorted by confidence
      const pos = state.allBoundingBoxes.findIndex((b) => b.class_index === classIndex)
      if (pos !== -1) {
        state.allBoundingBoxes[pos] = updatedBBox
      }
    },

    // Create: Add a new tag ID
    addEditingTagId: (state, action: PayloadAction<string>) => {
      console.log('addEditingTagId', action.payload)
      if (!state.editingTagIds.includes(action.payload)) {
        state.editingTagIds.push(action.payload)
      }
    },

    // Read: Selector for editingTagIds is defined outside the slice

    // Update: Set all editing tag IDs
    setEditingTagIds: (state, action: PayloadAction<string[]>) => {
      state.editingTagIds = action.payload
    },

    // Delete: Remove a specific tag ID
    removeEditingTagId: (state, action: PayloadAction<string>) => {
      state.editingTagIds = state.editingTagIds.filter((id) => id !== action.payload)
    },

    // Clear all editing tag IDs
    clearEditingTagIds: (state) => {
      state.editingTagIds = []
    },

    // Replace the existing undo/redo stack management with complete state handling
    undo: (state) => {
      if (state.undoStack.length > 0) {
        // Save current state to redo stack
        const currentState: UndoStackItem = {
          boundingBoxStatuses: { ...state.boundingBoxStatuses },
          rejected: [...state.rejectedBBoxIndexes],
          accepted: [...state.acceptedBBoxIndexes],
          all: [...state.allBoundingBoxes],
          editingTagIds: [...state.editingTagIds],
        }
        console.log('undo', currentState)
        state.redoStack = [currentState, ...state.redoStack]

        // Restore state from undo stack
        const previousState = state.undoStack[0]
        if (previousState.boundingBoxStatuses) {
          state.boundingBoxStatuses = previousState.boundingBoxStatuses
        }
        state.rejectedBBoxIndexes = previousState.rejected
        state.acceptedBBoxIndexes = previousState.accepted
        state.allBoundingBoxes = previousState.all
        if (previousState.editingTagIds) {
          state.editingTagIds = previousState.editingTagIds
        }

        // Remove used state from undo stack
        state.undoStack = state.undoStack.slice(1)
      }
    },

    redo: (state) => {
      if (state.redoStack.length > 0) {
        // Save current state to undo stack
        const currentState: UndoStackItem = {
          boundingBoxStatuses: { ...state.boundingBoxStatuses },
          rejected: [...state.rejectedBBoxIndexes],
          accepted: [...state.acceptedBBoxIndexes],
          all: [...state.allBoundingBoxes],
          editingTagIds: [...state.editingTagIds],
        }
        console.log('redo', currentState)
        state.undoStack = [currentState, ...state.undoStack]

        // Restore state from redo stack
        const nextState = state.redoStack[0]
        if (nextState.boundingBoxStatuses) {
          state.boundingBoxStatuses = nextState.boundingBoxStatuses
        }
        state.rejectedBBoxIndexes = nextState.rejected
        state.acceptedBBoxIndexes = nextState.accepted
        state.allBoundingBoxes = nextState.all
        if (nextState.editingTagIds) {
          state.editingTagIds = nextState.editingTagIds
        }

        // Remove used state from redo stack
        state.redoStack = state.redoStack.slice(1)
      }
    },

    // Add a new action to save state before modifications
    saveStateToHistory: (state) => {
      const currentState: UndoStackItem = {
        boundingBoxStatuses: { ...state.boundingBoxStatuses },
        rejected: [...state.rejectedBBoxIndexes],
        accepted: [...state.acceptedBBoxIndexes],
        all: [...state.allBoundingBoxes],
        editingTagIds: [...state.editingTagIds],
      }
      state.undoStack = [currentState, ...state.undoStack]
      console.log('saveStateToHistory', state.undoStack)
      // Clear redo stack when new changes are made
      state.redoStack = []
    },
  },
})

export const {
  setSortKey,
  toggleSortOrder,
  zoomIn,
  zoomOut,
  addAnnotatedContentIds,
  clearAnnotatedContentIds,

  clearUndoStack,

  clearRedoStack,

  setSelectedBoundingBox,

  setAllBoundingBoxes,
  mergeAllBoundingBoxes,
  clearAllBoundingBoxes,
  resetAllBoundingBoxes,

  // New action
  setBoundingBoxStatus,

  // Legacy actions kept for compatibility
  setAcceptedBoundingBoxes,
  addAcceptedBoundingBoxByIndex,
  removeAcceptedBoundingBoxByIndex,

  setRejectedBoundingBoxes,
  addRejectedBoundingBoxByIndex,
  removeRejectedBoundingBoxByIndex,

  setInitialBoundingBoxes,
  clearInitialBoundingBoxes,

  updateBoundingBox,
  addBoundingBox,
  addEditingTagId,
  setEditingTagIds,
  removeEditingTagId,
  clearEditingTagIds,
  setSelectedContentId,
  clearSelectedContentId,
  setAnnotatedPage,
  setUnannotatedPage,
  setPredictionsPage,
  setEditingContent,
  clearEditingContent,
  undo,
  redo,
  saveStateToHistory,
} = annotationSlice.actions

export const selectSortKey = (state: RootState) => state.annotation.sortKey
export const selectSortOrder = (state: RootState) => state.annotation.sortOrder
export const selectView = (state: RootState) => state.annotation.view
export const selectZoomLevel = (state: RootState) => state.annotation.zoomLevel
export const selectAnnotatedContentIds = (state: RootState) => state.annotation.annotatedContentIds

export const selectedBoundingBox = (state: RootState) => state.annotation.selectedBoundingBox
export const selectedBBoxClassIndex = (state: RootState) =>
  state.annotation.selectedBoundingBox?.class_index
export const selectRejectedBBoxIndexes = (state: RootState) => state.annotation.rejectedBBoxIndexes
export const selectAcceptedBBoxIndexes = (state: RootState) => state.annotation.acceptedBBoxIndexes
export const selectEditingTagIds = (state: RootState) => state.annotation.editingTagIds

export const selectInitialBoundingBoxes = (state: RootState) =>
  state.annotation.initialBoundingBoxes
export const selectSelectedContentId = (state: RootState) => state.annotation.selectedContentId
export const selectAnnotatedPage = (state: RootState) => state.annotation.annotatedPage
export const selectPredictionsPage = (state: RootState) => state.annotation.predictionsPage
export const selectUnannotatedPage = (state: RootState) => state.annotation.unannotatedPage
export const selectEditingContent = (state: RootState) => state.annotation.editingContent
export const selectTopUndoStackItem = (state: RootState) => state.annotation.undoStack?.[0]
export const selectTopRedoStackItem = (state: RootState) => state.annotation.redoStack?.[0]

// Memoized base selectors
export const selectAllBoundingBoxes = createSelector(
  [(state: RootState) => state.annotation.allBoundingBoxes],
  (allBoundingBoxes) =>
    [...allBoundingBoxes].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
)
export const selectBoundingBoxStatuses = (state: RootState) => state.annotation.boundingBoxStatuses

// Similarly, let's memoize other selectors that return new arrays
export const selectAllUntouchedBoundingBoxes = createSelector(
  [selectAllBoundingBoxes, selectBoundingBoxStatuses],
  (allBoxes, statuses) => {
    return allBoxes.filter(
      (box) =>
        statuses[box.class_index] === undefined ||
        statuses[box.class_index] === BBoxStatus.Untouched
    )
  }
)

export const selectRejectedBoundingBoxes = createSelector(
  [selectAllBoundingBoxes, selectBoundingBoxStatuses],
  (allBoxes, statuses) => {
    return allBoxes.filter((box) => statuses[box.class_index] === BBoxStatus.Rejected)
  }
)

export const selectAcceptedBoundingBoxes = createSelector(
  [selectAllBoundingBoxes, selectBoundingBoxStatuses],
  (allBoxes, statuses) => {
    return allBoxes.filter((box) => statuses[box.class_index] === BBoxStatus.Accepted)
  }
)

export default annotationSlice.reducer
